import cn from "classnames";
import { formatDistanceToNow } from "date-fns";
import { Suspense, useState } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import type {
	ConversationType,
	InboxConversationDto,
	InboxConversationFiltersDto,
	Platform,
} from "shared";
import { Icon } from "../common/Icon";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { AppPage } from "../layout/AppLayout";
import { InboxConversationList } from "./InboxConversationList";
import { InboxConversationView } from "./InboxConversationView";
import { InboxFilters } from "./InboxFilters";
import {
	InboxConversationListSkeleton,
	InboxMessagesSkeleton,
} from "./InboxSkeleton";
import { useInboxActions, useInboxSyncStatus } from "./useInbox";
import "./PageInbox.css";

export const PageInbox = () => {
	const [selectedConversation, setSelectedConversation] =
		useState<InboxConversationDto | null>(null);
	const [filters, setFilters] = useState<InboxConversationFiltersDto>({
		archived: false,
	});
	const { triggerSync } = useInboxActions();
	const { data: syncStatus } = useInboxSyncStatus();

	const isSyncing = syncStatus?.some((s) => s.status === "syncing");
	const syncErrors = syncStatus?.filter((s) => s.status === "error") ?? [];
	const lastSuccessfulSync = syncStatus
		?.map((s) => s.lastSyncAt)
		.filter((d): d is string => !!d)
		.sort()
		.pop();
	const hasNeverSynced =
		syncStatus !== undefined &&
		syncStatus.length > 0 &&
		!lastSuccessfulSync &&
		syncErrors.length === 0;

	const handlePlatformFilter = (platform: Platform | undefined) => {
		setFilters((prev) => ({ ...prev, platform }));
	};

	const handleTypeFilter = (conversationType: ConversationType | undefined) => {
		setFilters((prev) => ({ ...prev, conversationType }));
	};

	const handleArchivedToggle = () => {
		setFilters((prev) => ({ ...prev, archived: !prev.archived }));
	};

	return (
		<AppPage
			title="Inbox"
			subtitle="Messages and comments from your connected social accounts."
			headerActions={
				<Button
					onClick={() => triggerSync.mutate()}
					loading={triggerSync.isPending || isSyncing}
					variant="secondary"
					size="sm"
				>
					{isSyncing ? "Syncing..." : "Sync Now"}
				</Button>
			}
		>
			{syncErrors.length > 0 && (
				<Alert variant="danger" className="inbox-sync-alert">
					<div className="fw-bold mb-1">Sync failed</div>
					<ul className="mb-0 ps-3">
						{syncErrors.map((s) => (
							<li key={`${s.platform}-${s.syncType}`}>
								<strong>
									{s.platform} ({s.syncType.replace("_", " ")}):
								</strong>{" "}
								{s.errorMessage || "Unknown error"}
							</li>
						))}
					</ul>
				</Alert>
			)}
			{hasNeverSynced && (
				<Alert variant="info" className="inbox-sync-alert">
					This company has never completed an inbox sync. Click "Sync Now" to
					pull comments and direct messages from connected platforms.
				</Alert>
			)}
			{lastSuccessfulSync && (
				<div className="inbox-sync-meta">
					Last synced{" "}
					{formatDistanceToNow(new Date(lastSuccessfulSync), {
						addSuffix: true,
					})}
				</div>
			)}
			<Row
				className={cn(
					"inbox-container",
					selectedConversation && "inbox-container--detail",
				)}
			>
				<Col md={4} className="inbox-sidebar">
					<Card className="inbox-card h-100">
						<InboxFilters
							filters={filters}
							onPlatformFilter={handlePlatformFilter}
							onTypeFilter={handleTypeFilter}
							onArchivedToggle={handleArchivedToggle}
						/>
						<Suspense fallback={<InboxConversationListSkeleton />}>
							<InboxConversationList
								filters={filters}
								selectedId={selectedConversation?.id}
								onSelect={setSelectedConversation}
							/>
						</Suspense>
					</Card>
				</Col>
				<Col md={8} className="inbox-main">
					<Card className="inbox-card h-100">
						{selectedConversation ? (
							<Suspense fallback={<InboxMessagesSkeleton />}>
								<InboxConversationView
									conversationId={selectedConversation.id}
									onClose={() => setSelectedConversation(null)}
								/>
							</Suspense>
						) : (
							<div className="inbox-empty-state">
								<div className="inbox-empty-icon">
									<Icon name="inbox" size={36} />
								</div>
								<h4>Select a conversation</h4>
								<p>
									Choose a conversation from the list to view messages and
									reply.
								</p>
							</div>
						)}
					</Card>
				</Col>
			</Row>
		</AppPage>
	);
};
